import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { z } from 'zod'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useContact } from '@/stores/contacts.store'
import { useLeadsList } from '@/stores/leads.store'
import { useEventsList } from '@/stores/events.store'
import { useTasksList } from '@/stores/tasks.store'
import { useNotes } from '@/stores/notes.store'
import { useAttachments } from '@/stores/attachments.store'
import { useContactQuoteInteractions } from '@/stores/quotes.store'
import { ContactHeader } from '@/components/shared/contact-header'
import { ContactArchiveMenu } from '@/components/contacts/contact-archive-menu'
import { ContactEmailsList } from '@/components/contacts/contact-emails-list'
import { Badge } from '@/components/ui/badge'
import { InquiryStageBadge, EventStageBadge } from '@/components/shared/enum-badge'
import { ActivityTabs } from '@/components/shared/activity-tabs'
import { InquirySheet } from '@/components/inquiries/inquiry-sheet'
import { NewEventSheet } from '@/components/events/new-event-sheet'
import { RunWorkflowDialog } from '@/components/shared/run-workflow-dialog'
import { usePermissions } from '@/hooks/use-permissions'
import { ActionGate } from '@/components/shared/action-gate'
import { AccessDeniedState } from '@/components/shared/access-denied-state'
import { isPermissionDeniedError } from '@/lib/error-reporting'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import {
  Users,
  Phone,
  Building2,
  Calendar,
  FileText,
  CalendarDays,
  ExternalLink,
  Clock,
  UsersRound,
  Plus,
  Workflow,
} from 'lucide-react'
import { formatTimestamp } from '@/lib/date-utils'
import { formatPhoneNumber } from '@/lib/utils'

export const Route = createFileRoute('/_app/_crm/contacts/$id')({
  component: ContactDetailPage,
  validateSearch: z.object({
    from: z.string().optional(),
    tab: z.enum(['conversations']).optional(),
  }),
})

function ContactDetailPage() {
  const { id } = Route.useParams()
  const { tab } = Route.useSearch()

  useBreadcrumbFromRoute()

  const { contact, isLoading, isError, error } = useContact(id)
  const [leadSheetOpen, setLeadSheetOpen] = useState(false)
  const [eventSheetOpen, setEventSheetOpen] = useState(false)
  const [isRunWorkflowOpen, setIsRunWorkflowOpen] = useState(false)
  const { can } = usePermissions(contact?.organizationId || '')
  const canRunWorkflow = can('AutomationsManage')

  // Fetch related data
  const { inquiries, isLoading: inquiriesLoading } = useLeadsList({ contactId: id })
  const { events, isLoading: eventsLoading } = useEventsList({ contactId: id })
  const { tasks } = useTasksList({
    parentType: ParentTypeEnum.PARENT_TYPE_CONTACT,
    parentId: id
  })
  const { notes } = useNotes({
    parentType: ParentTypeEnum.PARENT_TYPE_CONTACT,
    parentId: id
  })
  const { attachments } = useAttachments({
    parentType: ParentTypeEnum.PARENT_TYPE_CONTACT,
    parentId: id
  })
  const {
    interactions: quoteInteractions,
    totalCount: quoteInteractionCount,
    isLoading: quoteInteractionsLoading,
  } = useContactQuoteInteractions({
    contactId: id,
    limit: 50,
    offset: 0,
  })

  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        {/* Header Skeleton */}
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-6 w-6 rounded" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-6 w-6 rounded" />
            </div>
          </div>
        </div>

        {/* Content Grid Skeleton */}
        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          {/* Left Column */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
              </CardHeader>
              <CardContent className="space-y-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-4 w-4" />
                    <div className="space-y-1 flex-1">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-4 w-48 mt-2" />
              </CardHeader>
              <CardContent>
                <div className="border border-dashed rounded-lg p-12">
                  <Skeleton className="h-5 w-5 mx-auto mb-2" />
                  <Skeleton className="h-4 w-32 mx-auto" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  if (isError || !contact) {
    if (isPermissionDeniedError(error)) {
      return <AccessDeniedState variant="page" noun="contacts" />
    }
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <Empty className="flex-1 border border-dashed py-12">
          <EmptyMedia variant="icon">
            <Users className="h-6 w-6" />
          </EmptyMedia>
          <EmptyDescription>We couldn't load this contact right now.</EmptyDescription>
        </Empty>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <ContactHeader
        firstName={contact.firstName}
        lastName={contact.lastName}
        email={contact.email}
        phoneNumber={contact.phoneNumber}
        contact={contact}
        currentContactId={contact.id}
        isArchived={!!contact.archivedAt}
        actionDropdown={<ContactArchiveMenu contact={contact} />}
      />

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        {/* Left Column - Contact Info & Companies */}
        <div className="space-y-4">
          {/* Contact Information Card */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ContactEmailsList contact={contact} />

              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div className="space-y-1 flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="text-sm">{contact.phoneNumber ? formatPhoneNumber(contact.phoneNumber) : 'Not provided'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div className="space-y-1 flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Created</p>
                  <p className="text-sm">
                    {contact.createdAt ? formatTimestamp(contact.createdAt, 'MMM d, yyyy') : '-'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div className="space-y-1 flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Last Updated</p>
                  <p className="text-sm">
                    {contact.updatedAt ? formatTimestamp(contact.updatedAt, 'MMM d, yyyy') : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <ActionGate
                organizationId={contact.organizationId}
                permission="AutomationsManage"
                verb="run"
                noun="workflows"
              >
                <Button variant="outline" onClick={() => setIsRunWorkflowOpen(true)}>
                  <Workflow className="h-4 w-4" />
                  Run Workflow
                </Button>
              </ActionGate>
            </CardContent>
          </Card>

          {/* Companies Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Companies
              </CardTitle>
              <CardDescription>
                Organizations this contact is associated with
              </CardDescription>
            </CardHeader>
            <CardContent>
              {contact.companies && contact.companies.length > 0 ? (
                <div className="space-y-2">
                  {contact.companies.map((company) => (
                    <Link
                      key={company.id}
                      to="/companies/$id"
                      params={{ id: company.id }}
                      className="flex items-center gap-2 p-2 rounded-md border hover:bg-muted/50 transition-colors group"
                    >
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium flex-1">{company.name}</span>
                      {company.archivedAt && <Badge variant="secondary">Archived</Badge>}
                      <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  ))}
                </div>
              ) : (
                <Empty className="border border-dashed py-8">
                  <EmptyMedia variant="icon">
                    <Building2 className="h-5 w-5" />
                  </EmptyMedia>
                  <EmptyDescription>No companies associated</EmptyDescription>
                </Empty>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Activity */}
        <div className="space-y-4">
          {/* Leads Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Leads
                  {inquiries.length > 0 && (
                    <span className="text-xs bg-muted px-2 py-0.5 rounded-full font-normal">
                      {inquiries.length}
                    </span>
                  )}
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => setLeadSheetOpen(true)}>
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  New Lead
                </Button>
              </div>
              <CardDescription>
                Leads from this contact
              </CardDescription>
            </CardHeader>
            <CardContent>
              {inquiriesLoading ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : inquiries.length > 0 ? (
                <div className="space-y-2">
                  {inquiries.map((inquiry) => (
                    <Link
                      key={inquiry.id}
                      to="/leads/$id"
                      params={{ id: inquiry.id }}
                      className="block p-3 rounded-md border hover:bg-muted/50 transition-colors group"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <InquiryStageBadge stage={inquiry.stage} />
                            {inquiry.firstEventDate && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <CalendarDays className="h-3 w-3" />
                                {formatTimestamp(inquiry.firstEventDate, 'MMM d, yyyy')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {inquiry.eventsCount > 0 && (
                              <span className="flex items-center gap-1">
                                <CalendarDays className="h-3 w-3" />
                                {inquiry.eventsCount} event{inquiry.eventsCount === 1 ? '' : 's'}
                              </span>
                            )}
                            {inquiry.quoteTotal && (
                              <span className="font-medium text-foreground">
                                ${(inquiry.quoteTotal / 100).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <Empty className="border border-dashed py-8">
                  <EmptyMedia variant="icon">
                    <FileText className="h-5 w-5" />
                  </EmptyMedia>
                  <EmptyDescription>No leads yet</EmptyDescription>
                </Empty>
              )}
            </CardContent>
          </Card>

          {/* Events Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  Events
                  {events.length > 0 && (
                    <span className="text-xs bg-muted px-2 py-0.5 rounded-full font-normal">
                      {events.length}
                    </span>
                  )}
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => setEventSheetOpen(true)}>
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  New Event
                </Button>
              </div>
              <CardDescription>
                Scheduled and past events for this contact
              </CardDescription>
            </CardHeader>
            <CardContent>
              {eventsLoading ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : events.length > 0 ? (
                <div className="space-y-2">
                  {events.map((event) => (
                    <Link
                      key={event.id}
                      to="/events/$id"
                      params={{ id: event.id }}
                      className="block p-3 rounded-md border hover:bg-muted/50 transition-colors group"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <EventStageBadge stage={event.stage} />
                            {event.eventDate && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <CalendarDays className="h-3 w-3" />
                                {formatTimestamp(event.eventDate, 'MMM d, yyyy')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {event.eventLength && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {event.eventLength}h
                              </span>
                            )}
                            {event.eventGuests && (
                              <span className="flex items-center gap-1">
                                <UsersRound className="h-3 w-3" />
                                {event.eventGuests} guests
                              </span>
                            )}
                          </div>
                        </div>
                        <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <Empty className="border border-dashed py-8">
                  <EmptyMedia variant="icon">
                    <CalendarDays className="h-5 w-5" />
                  </EmptyMedia>
                  <EmptyDescription>No events yet</EmptyDescription>
                </Empty>
              )}
            </CardContent>
          </Card>

          {/* Activity */}
          <ActivityTabs
            organizationId={contact.organizationId}
            parentType={ParentTypeEnum.PARENT_TYPE_CONTACT}
            parentId={contact.id}
            notes={notes}
            tasks={tasks}
            attachments={attachments}
            quoteInteractions={quoteInteractions}
            quoteInteractionCount={quoteInteractionCount}
            quoteInteractionsLoading={quoteInteractionsLoading}
            customerEmail={contact.email}
            emailContactId={contact.id}
            initialTab={tab}
          />
        </div>
      </div>

      <InquirySheet
        mode="create"
        open={leadSheetOpen}
        onOpenChange={setLeadSheetOpen}
        defaultContactId={contact.id}
      />
      <NewEventSheet
        open={eventSheetOpen}
        onOpenChange={setEventSheetOpen}
        defaultContactId={contact.id}
      />
      {canRunWorkflow && (
        <RunWorkflowDialog
          sourceResourceId={contact.id}
          triggerEntityType="CONTACT"
          open={isRunWorkflowOpen}
          onOpenChange={setIsRunWorkflowOpen}
        />
      )}
    </div>
  )
}
